/**
 * The icon library for Celus. Every icon in the UI comes from here - no bare
 * `mdi-*` strings in components - so one concept always looks the same and a
 * glyph can be swapped in a single place. Add new icons here, in alphabetical
 * order, and check the list first: the name you want may already exist.
 *
 * Registered globally by plugins/icons.ts, so a template can use it without
 * importing anything:
 *
 *   <v-icon :icon="$icons.edit" />
 *
 */

export const ICONS = {
  access_point: "mdi-access-point",
  adjust: "mdi-circle-half-full",
  all: "mdi-fullscreen",
  ambulance: "mdi-ambulance",
  annotation: "mdi-note",
  arrow_down: "mdi-arrow-down",
  arrow_down_circle: "mdi-arrow-down-circle-outline",
  arrow_left: "mdi-arrow-left",
  arrow_right: "mdi-arrow-right",
  arrow_up: "mdi-arrow-up",
  arrow_up_circle: "mdi-arrow-up-circle-outline",
  bars: "mdi-menu",
  bell: "mdi-bell",
  bell_off: "mdi-bell-off",
  book: "mdi-book-open-page-variant",
  book_open: "mdi-book-open-variant",
  border_all: "mdi-border-all",
  bug: "mdi-bug",
  building: "mdi-office-building",
  bullhorn: "mdi-bullhorn",
  calendar: "mdi-calendar-outline",
  canceled: "mdi-cancel",
  cellphone: "mdi-cellphone",
  chart_line: "mdi-chart-line",
  check_circle: "mdi-record-circle",
  check_square: "mdi-checkbox-marked",
  check_square_outline: "mdi-checkbox-marked-outline",
  chevron_double_down: "mdi-chevron-double-down",
  chevron_double_left: "mdi-chevron-double-left",
  chevron_double_right: "mdi-chevron-double-right",
  chevron_down: "mdi-chevron-down",
  chevron_left: "mdi-chevron-left",
  chevron_right: "mdi-chevron-right",
  chevron_up: "mdi-chevron-up",
  clear: "mdi-close",
  click: "mdi-cursor-default-click-outline",
  clipboard: "mdi-clipboard-outline",
  clock: "mdi-clock-outline",
  close_circle: "mdi-close-circle",
  cloud: "mdi-cloud-download",
  cloud_upload: "mdi-cloud-upload",
  cogs: "mdi-cogs",
  comment: "mdi-comment-outline",
  copy: "mdi-content-copy",
  crown: "mdi-crown",
  dashboard: "mdi-view-dashboard",
  database: "mdi-database",
  delete: "mdi-trash-can-outline",
  dollar: "mdi-currency-usd",
  done: "mdi-check",
  dots: "mdi-dots-horizontal",
  download: "mdi-tray-arrow-down",
  edit: "mdi-square-edit-outline",
  email: "mdi-email",
  email_open: "mdi-email-open",
  empty: "mdi-close-circle-outline",
  error: "mdi-alert-circle",
  exchange_alt: "mdi-swap-horizontal",
  exclamation: "mdi-exclamation",
  expand: "mdi-arrow-expand-all",
  file: "mdi-file-outline",
  file_download: "mdi-file-download-outline",
  file_excel: "mdi-file-excel-outline",
  file_export: "mdi-file-export",
  file_import: "mdi-file-import-outline",
  file_search: "mdi-file-search-outline",
  flag: "mdi-flag-variant-outline",
  folder: "mdi-folder-outline",
  folder_open: "mdi-folder-open-outline",
  forward: "mdi-fast-forward",
  gamepad: "mdi-gamepad-variant",
  globe: "mdi-web",
  hand_right: "mdi-hand-pointing-right",
  hidden: "mdi-eye-off-outline",
  history: "mdi-history",
  info: "mdi-information",
  key: "mdi-key",
  layers: "mdi-layers",
  lightbulb: "mdi-lightbulb",
  list: "mdi-format-list-bulleted",
  list_box: "mdi-view-list-outline",
  loader: "mdi-loading",
  lock: "mdi-lock-outline",
  map: "mdi-map-outline",
  masks: "mdi-drama-masks",
  minus: "mdi-minus",
  minus_circle: "mdi-minus-circle",
  newspaper: "mdi-newspaper-variant-outline",
  open_detail: "mdi-arrow-right-bold-box-outline",
  open_in_app: "mdi-open-in-app",
  open_in_new_tab: "mdi-open-in-new",
  organization: "mdi-bank-outline",
  palette: "mdi-palette-outline",
  paused: "mdi-pause-circle-outline",
  pen: "mdi-fountain-pen-tip",
  pending: "mdi-timer-sand",
  play: "mdi-play",
  plus: "mdi-plus",
  plus_circle: "mdi-plus-circle",
  processing: "mdi-cog",
  question: "mdi-help",
  ready: "mdi-play-circle-outline",
  refresh: "mdi-refresh",
  registered: "mdi-registered-trademark",
  repeat: "mdi-repeat",
  reset: "mdi-restore",
  sad: "mdi-emoticon-sad-outline",
  save: "mdi-content-save",
  save_outline: "mdi-content-save-outline",
  scroll: "mdi-script-text-outline",
  search: "mdi-magnify",
  show_chart: "mdi-chart-box-outline",
  show_details: "mdi-magnify-scan",
  sitemap: "mdi-sitemap",
  square: "mdi-square",
  star: "mdi-star",
  stop: "mdi-stop",
  success: "mdi-check-circle",
  table: "mdi-table",
  tag: "mdi-tag",
  tags: "mdi-tag-multiple",
  thumb_up: "mdi-thumb-up",
  tools: "mdi-tools",
  unchecked_circle: "mdi-circle-outline",
  unchecked_square: "mdi-checkbox-blank-outline",
  unknown: "mdi-help-circle",
  unlink: "mdi-link-off",
  unlock: "mdi-lock-open-variant-outline",
  upload: "mdi-tray-arrow-up",
  user: "mdi-account",
  user_check: "mdi-account-check-outline",
  user_plus: "mdi-account-plus",
  verify: "mdi-email-open-outline",
  visible: "mdi-eye-outline",
  warning: "mdi-alert",
} as const;

export type IconName = keyof typeof ICONS;
export type IconGlyph = (typeof ICONS)[IconName];
